import dayjs from 'dayjs'
import 'dayjs/locale/th'
import tailwindMergeConfig from './tailwind-merge'
import { getLocale } from '@/store/useLocaleStore'
import { SimpleUserResponse } from '@/types/response'
import { clsx, type ClassValue } from 'cn'
import { extendTailwindMerge } from 'cn/config'

const twMerge = extendTailwindMerge(tailwindMergeConfig)
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const isFunction = (value: unknown) =>
  value
    ? Object.prototype.toString.call(value) === '[object Function]' ||
      'function' === typeof value ||
      value instanceof Function
    : false

export function formatIsoDuration(value: string | number | null | undefined): string {
  if (!value) return '-'
  const str = String(value)
  const match = str.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/)
  if (!match) return str
  let totalSeconds = Math.floor(parseFloat(match[4] ?? '0'))
  const d = parseInt(match[1] ?? '0')
  const extraH = Math.floor(totalSeconds / 3600)
  totalSeconds %= 3600
  const extraM = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  const h = parseInt(match[2] ?? '0') + extraH
  const m = parseInt(match[3] ?? '0') + extraM
  const parts = []
  if (d > 0) parts.push(`${d}d`)
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  if (s > 0) parts.push(`${s}s`)
  return parts.length ? parts.join(' ') : '0s'
}

export function formatFileSize(fileSize: number) {
  if (fileSize < 1024 * 1024) return `${Math.max(1, Math.round(fileSize / 1024))} KB`
  return `${(fileSize / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`
}

export function formatNumber(
  value: number | null | undefined,
  locales?: Intl.LocalesArgument,
  options?: Intl.NumberFormatOptions,
): string {
  return typeof value === 'number' ? value.toLocaleString(locales, options) : '-'
}

/**
 * Formats a date string using dayjs
 * @param value - The date string to format (ISO format or dayjs-compatible string)
 * @param dateOnly - If true, returns 'DD-MM-YYYY', otherwise 'DD-MM-YYYY, HH:mm:ss'
 * @returns Formatted date string or '-' if value is null/empty
 */
export function formatDateTime(
  value: string | Date | null,
  dateOnly = false,
  customFormat: string | undefined = undefined,
): string {
  const locale = getLocale()
  if (!value) return '-'
  const dateTime = dayjs(value).locale(locale)
  if (customFormat) return dateTime.format(customFormat)
  let format = ''
  switch (locale) {
    case 'th':
      format = dateOnly ? 'DD/MM/YYYY' : 'DD/MM/YYYY เวลา HH:mm น.'
      break
    default:
      format = dateOnly ? 'MMM DD,YYYY' : 'MMM DD,YYYY [at] h:mm A'
      break
  }
  return dateTime.format(format)
}

export function formatMonthValue(date: Date): string {
  return formatDateTime(date, false, 'YYYY-MM')
}

export function getDurationInDays(startDateTime: string, endDateTime: string): number {
  return Math.max(1, dayjs(endDateTime).startOf('day').diff(dayjs(startDateTime).startOf('day'), 'day') + 1)
}

export function combineDateAndTime(date: Date, time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  const dateTime = new Date(date)
  dateTime.setHours(hours, minutes, 0, 0)
  return dateTime.toISOString()
}

export function normalize_log_action_type(action: string): string {
  return action.replaceAll('.', '_')
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function replaceUrl(url: string | URL) {
  if (typeof window === 'undefined') return

  const nextUrl = url.toString()
  if (window.location.href === new URL(nextUrl, window.location.href).href) return

  window.history.replaceState(null, '', nextUrl)
}

export function getSelectionKey(values: string[]) {
  return [...values].sort().join(',')
}

export function getTagLabel(
  category: string,
  tag: string,
  tagItems: Record<string, { value: string; label: string }[]>,
) {
  return tagItems[category]?.find((item) => item.value === tag)?.label ?? tag
}

export function getSummaryGroups(
  conditions: Array<{
    items: Array<{
      category: string
      tag: string
    }>
  }>,
  tagItems: Record<string, { value: string; label: string }[]>,
) {
  return conditions
    .map((condition) =>
      condition.items
        .filter((item) => item.category && item.tag)
        .map((item) => getTagLabel(item.category, item.tag, tagItems)),
    )
    .filter((tags) => tags.length > 0)
}

export function serializeListQueryParam(values?: Array<string | number>) {
  return values?.length ? values.join(',') : undefined
}

export function formatRangeDate(start: string | null, end: string | null): string {
  if (!start || !end) return '—'
  return `${start} - ${end}`
}

export function formatUser(user: SimpleUserResponse | null) {
  if (!user) return '-'
  return `${user.first_name} ${user.last_name}`.trim() || user.username
}
